import { useState } from "react";
import { DollarSign, TrendingUp, Info } from "lucide-react";
import { formatUSD } from "@/lib/format";

const formatCompactUSD = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const num = Math.abs(Number(n));
  if (num >= 1e9) return "$" + (num / 1e9).toFixed(2).replace(/\.?0+$/, "") + "B";
  if (num >= 1e6) return "$" + (num / 1e6).toFixed(2).replace(/\.?0+$/, "") + "M";
  if (num >= 1e3) return "$" + (num / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
  return "$" + Math.round(num).toLocaleString("en-US");
};

const CPM_OPTIONS = [
  { key: "low", label: "Low CPM", desc: "$0.50" },
  { key: "mid", label: "Average CPM", desc: "$2.50" },
  { key: "high", label: "High CPM", desc: "$6.00" },
];

export default function RevenuePanel({ revenue }) {
  const [tab, setTab] = useState("mid");
  if (!revenue) return null;
  const active = revenue[tab];

  const Row = ({ label, value, testId, isLast = false }) => (
    <div
      className={`flex items-baseline justify-between py-5 ${
        isLast ? "" : "border-b border-border/50"
      }`}
    >
      <span className="text-[15px] font-medium text-muted-foreground">{label}</span>
      <span
        data-testid={testId}
        className="text-[32px] font-semibold tabular-nums font-mono text-foreground"
        style={{ letterSpacing: "-0.025em" }}
      >
        {formatUSD(value)}
      </span>
    </div>
  );

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
      <div className="flex items-end justify-between mb-8">
        <h2 className="text-[32px] font-bold tracking-tight leading-none">
          Estimated Revenue
        </h2>
        <div className="overline">Average YouTube CPM</div>
      </div>

      <div
        data-testid="revenue-panel"
        className="grid md:grid-cols-2 gap-6"
      >
        {/* ------- Revenue Simulator ------- */}
        <div
          className="rounded-2xl bg-card border border-border/60 p-8 sm:p-10"
          style={{ boxShadow: "0 1px 2px rgba(17,24,39,0.03), 0 8px 32px -12px rgba(17,24,39,0.06)" }}
        >
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
              <DollarSign className="w-4.5 h-4.5 text-primary" style={{ width: 18, height: 18 }} />
            </div>
            <h3 className="text-[22px] font-semibold tracking-tight">Revenue Simulator</h3>
          </div>

          {/* Custom CPM Tabs — pill-style */}
          <div
            data-testid="revenue-cpm-tabs"
            role="tablist"
            className="grid grid-cols-3 gap-1.5 p-1.5 bg-secondary/50 rounded-full mb-2"
          >
            {CPM_OPTIONS.map((opt) => {
              const selected = tab === opt.key;
              return (
                <button
                  key={opt.key}
                  role="tab"
                  aria-selected={selected}
                  data-testid={`cpm-${opt.key}`}
                  onClick={() => setTab(opt.key)}
                  className={`relative rounded-full py-2.5 px-3 text-[13px] font-semibold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                    selected
                      ? "bg-white text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground hover:bg-white/60"
                  }`}
                  style={selected ? { boxShadow: "0 2px 8px -2px rgba(17,24,39,0.08)" } : undefined}
                >
                  <span className="block leading-none">{opt.label}</span>
                  <span className={`block text-[10px] font-mono mt-1 leading-none ${selected ? "text-primary" : "text-muted-foreground/70"}`}>
                    {opt.desc}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-2">
            <Row testId="rev-daily" label="Daily Revenue" value={active.daily} />
            <Row testId="rev-monthly" label="Monthly Revenue" value={active.monthly} />
            <Row testId="rev-annual" label="Annual Revenue" value={active.annual} isLast />
          </div>

          <div className="mt-6 flex items-start gap-2 text-[13px] text-muted-foreground leading-relaxed">
            <Info className="w-3.5 h-3.5 mt-1 shrink-0 opacity-70" />
            <span>
              Estimates use average YouTube CPMs ($0.50 low, $2.50 mid, $6.00 high) with the standard 55% creator share. Real payouts vary by niche, geography, and seasonality.
            </span>
          </div>
        </div>

        {/* ------- Annual Revenue Range ------- */}
        <div
          className="rounded-2xl bg-card border border-border/60 p-8 sm:p-10 flex flex-col"
          style={{ boxShadow: "0 1px 2px rgba(17,24,39,0.03), 0 8px 32px -12px rgba(17,24,39,0.06)" }}
        >
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <TrendingUp className="w-4.5 h-4.5 text-emerald-600" style={{ width: 18, height: 18 }} />
            </div>
            <h3 className="text-[22px] font-semibold tracking-tight">Annual Revenue Range</h3>
          </div>

          <div className="grid grid-cols-3 gap-3 flex-1 items-end py-4">
            <div className="text-center min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-3">
                Minimum
              </div>
              <div
                data-testid="rev-min"
                className="text-[22px] font-semibold tabular-nums font-mono text-foreground"
                style={{ letterSpacing: "-0.02em" }}
                title={formatUSD(revenue.low.annual)}
              >
                {formatCompactUSD(revenue.low.annual)}
              </div>
            </div>
            <div className="text-center relative min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary mb-3">
                Average
              </div>
              <div
                data-testid="rev-mid-annual"
                className="text-[30px] font-semibold tabular-nums font-mono text-primary"
                style={{ letterSpacing: "-0.03em" }}
                title={formatUSD(revenue.mid.annual)}
              >
                {formatCompactUSD(revenue.mid.annual)}
              </div>
              <div className="absolute -inset-2 -z-10 rounded-2xl bg-primary/[0.04]" aria-hidden="true" />
            </div>
            <div className="text-center min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-3">
                Maximum
              </div>
              <div
                data-testid="rev-max"
                className="text-[22px] font-semibold tabular-nums font-mono text-foreground"
                style={{ letterSpacing: "-0.02em" }}
                title={formatUSD(revenue.high.annual)}
              >
                {formatCompactUSD(revenue.high.annual)}
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-border/50 space-y-3">
            <div className="flex items-baseline justify-between">
              <span className="text-[14px] text-muted-foreground">Estimated views/month</span>
              <span className="text-[15px] font-semibold tabular-nums font-mono text-foreground">
                {revenue.monthly_views_estimate.toLocaleString("en-US")}
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-[14px] text-muted-foreground">Uploads/month</span>
              <span className="text-[15px] font-semibold tabular-nums font-mono text-foreground">
                {revenue.videos_per_month_estimate}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
